import type {
  ArtistRef,
  ArtistSupportPayload,
  InboxItem,
  InboxSection,
  MusicRecommendationsPayload,
  ImportedMusicPayload,
  UpcomingConcertsPayload,
  TrackRef,
} from "./InboxDetailSheet/types";

const compactList = (values: (string | null | undefined)[]) => {
  const uniqueValues = [...new Set(values.filter(Boolean) as string[])];
  if (uniqueValues.length <= 2) return uniqueValues.join(" and ");
  return `${uniqueValues.slice(0, 2).join(", ")} and more`;
};

const pluralize = (count: number, singular: string) =>
  `${count} ${singular}${count === 1 ? "" : "s"}`;

/** Keep the highest-ranked recommendation for each artist. */
export const getTopTrackPerArtist = (tracks: TrackRef[]): TrackRef[] => {
  const seenArtists = new Set<string>();
  return tracks.filter((track) => {
    const artistKey =
      track.artistName.trim().toLocaleLowerCase() || track.songId;
    if (seenArtists.has(artistKey)) return false;
    seenArtists.add(artistKey);
    return true;
  });
};

/** Imported-song examples belong to the delta and are capped independently of artists. */
export const getImportedMusicTracks = (
  payload: ImportedMusicPayload,
): TrackRef[] => {
  const delta = payload.newlyMatchedSongsCount;
  if (typeof delta !== "number" || delta <= 0) return [];
  const seen = new Set<string>();
  return (payload.tracks ?? [])
    .filter((track) => {
      if (seen.has(track.songId)) return false;
      seen.add(track.songId);
      return true;
    })
    .slice(0, Math.min(3, delta));
};

/** The artists named by the notification, in display order. */
export const getSupportedArtists = (item?: InboxItem): ArtistRef[] => {
  const supportSection = item?.content?.sections.find(
    (section) => section.item === "artist_support",
  );
  if (!supportSection) return [];

  const artists =
    (supportSection.payload as ArtistSupportPayload).artists ?? [];
  const seenArtistIds = new Set<string>();
  return artists.filter((artist) => {
    if (!artist.id || seenArtistIds.has(artist.id)) return false;
    seenArtistIds.add(artist.id);
    return true;
  });
};

/**
 * Artist-support notifications replace their unrelated general recommendation
 * block with songs fetched directly from the artists named in the message.
 */
export const getInboxDetailSections = (item?: InboxItem): InboxSection[] => {
  const sections = item?.content?.sections ?? [];
  const hasSupportedArtists = getSupportedArtists(item).length > 0;

  return sections.filter(
    (section) =>
      section.item !== "artist_support" &&
      (!hasSupportedArtists || section.item !== "music_recommendations"),
  );
};

/**
 * Prefer the notification's actual payload over generic campaign preview copy.
 * Existing/legacy items without rich content keep their stored subtitle.
 */
export const getInboxItemSummary = (item: InboxItem): string => {
  const section = item.content?.sections[0];
  if (!section) return item.subtitle;

  switch (section.item) {
    case "artist_support": {
      const { artists } = section.payload as ArtistSupportPayload;
      const names = compactList(artists.map((artist) => artist.name));
      return names ? `Your plays supported ${names}` : section.text;
    }
    case "music_recommendations": {
      const { tracks } = section.payload as MusicRecommendationsPayload;
      const artists = compactList(
        getTopTrackPerArtist(tracks).map((track) => track.artistName),
      );
      return artists ? `New picks from ${artists}` : section.text;
    }
    case "imported_music": {
      const payload = section.payload as ImportedMusicPayload;
      if (typeof payload.newlyMatchedSongsCount === "number") {
        return `${pluralize(payload.newlyMatchedSongsCount, "new song")} matched`;
      }
      return [
        pluralize(payload.matchedSongsCount, "song"),
        pluralize(payload.importedArtistCount ?? 0, "artist"),
        pluralize(payload.importedPlaylistCount ?? 0, "playlist"),
      ].join(" · ");
    }
    case "upcoming_concerts": {
      const { concerts } = section.payload as UpcomingConcertsPayload;
      const concert = concerts[0];
      if (!concert) return section.text;
      return [concert.artistName, concert.city].filter(Boolean).join(" · ");
    }
    default:
      return section.text || item.subtitle;
  }
};

export const getInboxItemRoute = (itemId: string) => ({
  pathname: "/(app)/(tabs)/inbox/[inboxItemId]" as const,
  params: { inboxItemId: itemId },
});
